import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';

import { SelectFieldOption } from '@hrlogics/react-helper-types';

import { formatPhoneNumber, numberWithCommas } from '@clearverify/utils';

import { PartnershipInfoFragment, useGetPartnershipsQuery } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { Table, TableColumn, TableRowAction, TableTopAction } from 'src/components/table';
import { COMPANY_SEARCH_CATEGORY_MAP } from 'src/constants/company';
import { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch, useSearchQueryVariables } from 'src/hooks/common';
import { useModalV2 } from 'src/hooks/shared';
import { InvitePartnershipByPlatformStaffModal } from 'src/modals/partnership';
import { buildPartnershipDetailsUrl } from 'src/utils/peo';

import { prepareSearchFilterByCategories } from './utils';

export const PartnershipsTable: React.FC = () => {
  const navigate = useNavigate();

  const { openModal } = useModalV2();

  const { setSearchValue, setSearchCategory } = useSearch();
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilterByCategories);

  const { pagination, paginationQueryVariables } = useFilterStorage();

  const { data, loading, refetch } = useGetPartnershipsQuery({
    variables: {
      where: searchQueryVariables,
      take: paginationQueryVariables.first,
      skip: paginationQueryVariables.skip,
    },
  });

  const refetchData = React.useCallback(async () => {
    await refetch();
    pagination.resetPage();
  }, [pagination, refetch]);

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => {
          openModal(InvitePartnershipByPlatformStaffModal, {
            id: ModalId.InvitePartnershipByPlatformStaff,
          });
        },
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              variant="contained"
              startIcon={this.icon}
              disabled={loading}
              loading={loading}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [loading, openModal],
  );

  const rowActions: Array<TableRowAction<PartnershipInfoFragment>> = React.useMemo(
    () => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: partnership => {
          navigate(buildPartnershipDetailsUrl(partnership.id, partnership.companyType));
        },
      },
    ],
    [navigate],
  );

  return (
    <Table
      dataSource={data?.getPartnerships.items || []}
      rowCount={data?.getPartnerships.count || 0}
      columns={columns}
      topActions={topActions}
      onSearch={setSearchValue}
      onSearchCategorySelect={setSearchCategory}
      rowActions={rowActions}
      loading={loading}
      pagination={pagination}
      onRefresh={refetchData}
      searchCategories={PARTNERSHIP_SEARCH_CATEGORIES}
    />
  );
};

const columns: Array<TableColumn<PartnershipInfoFragment>> = [
  {
    title: 'Company Name',
    key: 'displayName',
    sortable: true,
    render: (_valueByDataPath, partnership) => {
      return (
        <ClickableLabel
          type="link"
          to={buildPartnershipDetailsUrl(partnership.id, partnership.companyType)}
          label={partnership.displayName}
        />
      );
    },
  },
  {
    title: 'Contact Email',
    key: 'contactEmail',
    sortable: true,
    render: (_valueByDataPath, partnership) => {
      return <DemoPrivacyText>{partnership.contactEmail}</DemoPrivacyText>;
    },
  },
  {
    title: 'Contact Phone',
    key: 'contactPhoneNumber',
    render: (_valueByDataPath, partnership) => {
      return <DemoPrivacyText>{formatPhoneNumber(partnership.contactPhoneNumber)}</DemoPrivacyText>;
    },
  },
  {
    title: 'Employer Count',
    key: 'employerCount',
    render: (_valueByDataPath, partnership) => {
      return numberWithCommas(partnership.employerCount);
    },
  },
  {
    title: 'Status',
    key: 'status',
    render: (_valueByDataPath, partnership) => {
      return <UserStatusChip userStatus={partnership.status} />;
    },
  },
];

const PARTNERSHIP_SEARCH_CATEGORIES = [
  { label: 'Display Name', value: COMPANY_SEARCH_CATEGORY_MAP.displayName },
  { label: 'Staff Email', value: COMPANY_SEARCH_CATEGORY_MAP.staffEmail },
] as const satisfies SelectFieldOption[];
